import { useState, useEffect } from 'react';
import { powerBiService, healthCheck } from '../services/api';

export const usePowerBI = (initialTier = 'Beta') => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [embedData, setEmbedData] = useState(null);
  const [currentTier, setCurrentTier] = useState(initialTier);
  const [serverStatus, setServerStatus] = useState('checking');

  const checkServerHealth = async () => {
    try {
      const isHealthy = await healthCheck();
      setServerStatus(isHealthy ? 'healthy' : 'unhealthy');
      return isHealthy;
    } catch (error) {
      console.error('Server health check failed:', error);
      setServerStatus('unhealthy');
      return false;
    }
  };

  const loadDashboard = async (tier = currentTier) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Check server health first
      const isServerHealthy = await checkServerHealth();
      if (!isServerHealthy) {
        throw new Error('Backend server is not available. Please ensure the server is running');
      }

      setEmbedData(null);
      setCurrentTier(tier);
    } catch (err) {
      console.error('Failed to load PowerBI dashboard:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshDataset = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const refreshData = await powerBiService.refreshDataset();
      
      if (refreshData.status === 'already_in_progress') {
        return { status: 'already_in_progress', currentRefreshId: refreshData.currentRefreshId };
      }

      return refreshData;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const pollRefreshStatus = async (refreshId, onStatusUpdate) => {
    const maxPolls = 60; // 30 minutes max (30 second intervals)
    let pollCount = 0;

    while (pollCount < maxPolls) {
      try {
        pollCount++;
        const statusData = await powerBiService.getRefreshStatus(refreshId);
        
        if (onStatusUpdate) {
          onStatusUpdate(statusData);
        }

        if (statusData.isComplete) {
          return statusData;
        }

        await new Promise(resolve => setTimeout(resolve, 30000)); // Wait 30 seconds
      } catch (error) {
        console.error(`Polling error (attempt ${pollCount}):`, error);
        if (pollCount >= maxPolls) {
          throw new Error('Lost connection to refresh status');
        }
        await new Promise(resolve => setTimeout(resolve, 30000));
      }
    }

    throw new Error('Refresh status polling timed out');
  };

  const changeTier = async (newTier) => {
    if (newTier !== currentTier) {
      await loadDashboard(newTier);
    }
  };

  useEffect(() => {
    loadDashboard(initialTier);
  }, [initialTier]);

  return {
    isLoading,
    error,
    embedData,
    currentTier,
    serverStatus,
    refreshDataset,
    pollRefreshStatus,
    reloadDashboard: loadDashboard,
    changeTier,
    checkServerHealth
  };
};

export default usePowerBI;
